import { useState } from 'react'
import { Plus, Pencil, Trash2, Tag, Building2, Check, Search } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { useMe } from '@/hooks/useMe'
import { useProviderTagsManagement } from '@/hooks/useProviderTagsManagement'
import { useProviderTagApplications } from '@/hooks/useProviderTagApplications'
import { useAllClinics } from '@/hooks/useAllClinics'
import { providerTagService } from '@/services/provider_tag_service'
import type { ProviderTagWithUsage } from '@magnetar/admin-api-interfaces'

const TAG_NAME_MAX = 50

export default function TagManagementPage() {
  const { token } = useAuth()
  const { user } = useMe()
  const { mode, id: viewModeId } = useViewMode()

  // sys_admin 在 provider mode 用 viewMode 的 id；provider user 由 backend 從 JWT 解析
  // 但前端 hook 永遠帶 provider_id，admin 與 provider 邏輯一致
  const isSysAdmin = user?.role_type === 'sys_admin'
  const providerId =
    isSysAdmin && mode === 'provider' && viewModeId ? viewModeId : user?.provider_id ?? null

  const { items, isLoading, mutate } = useProviderTagsManagement({
    providerId,
    type: 'clinic',
  })

  const [formOpen, setFormOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<ProviderTagWithUsage | null>(null)
  const [formName, setFormName] = useState('')
  const [editTab, setEditTab] = useState<'name' | 'apply'>('name')
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ProviderTagWithUsage | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const openCreate = () => {
    setEditingTag(null)
    setFormName('')
    setEditTab('name')
    setFormOpen(true)
  }

  const openEdit = (tag: ProviderTagWithUsage) => {
    setEditingTag(tag)
    setFormName(tag.name)
    setEditTab('name')
    setFormOpen(true)
  }

  const handleSubmit = async () => {
    if (!token) return
    const cleanedName = formName.trim()
    if (!cleanedName || cleanedName.length > TAG_NAME_MAX) return
    if (editingTag && cleanedName === editingTag.name) {
      // 沒變，直接關
      setFormOpen(false)
      return
    }
    setSubmitting(true)
    try {
      if (editingTag) {
        await providerTagService.rename(editingTag.provider_tag_id, { name: cleanedName }, token)
      } else {
        await providerTagService.create(
          {
            type: 'clinic',
            name: cleanedName,
            ...(isSysAdmin && providerId ? { provider_id: providerId } : {}),
          },
          token,
        )
      }
      setFormOpen(false)
      mutate()
    } catch {
      // toast 已在 service 中處理
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!token || !deleteTarget) return
    try {
      await providerTagService.delete(deleteTarget.provider_tag_id, token)
      setDeleteTarget(null)
      setDeleteConfirmText('')
      mutate()
    } catch {
      // toast 已在 service 中處理
    }
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
  }

  // Delete confirm UX：使用診所數 > 0 時要求打字確認，避免誤觸發 cascade
  const requiresTypedConfirm = (deleteTarget?.usage_count ?? 0) > 0
  const canConfirmDelete = !requiresTypedConfirm || deleteConfirmText === deleteTarget?.name

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="工具箱"
        title="標籤管理"
        description="管理 Provider 旗下診所共用的標籤名稱"
        icon={<Tag className="h-8 w-8" />}
        level="section"
      >
        <Button onClick={openCreate} disabled={!providerId}>
          <Plus className="h-4 w-4 mr-2" />
          新增標籤
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="pt-6">
          {!providerId ? (
            <p className="text-center text-muted-foreground py-8">
              請先選擇 Provider scope
            </p>
          ) : isLoading ? (
            <p className="text-center text-muted-foreground py-8">載入中...</p>
          ) : items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              尚無標籤；新增的標籤會在此 Provider 旗下所有診所的標籤輸入框中可被搜尋
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>標籤名稱</TableHead>
                  <TableHead className="w-[120px]">使用診所數</TableHead>
                  <TableHead className="w-[160px]">建立時間</TableHead>
                  <TableHead className="w-[120px]">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.provider_tag_id}>
                    <TableCell>
                      <Badge variant="secondary">{item.name}</Badge>
                    </TableCell>
                    <TableCell>{item.usage_count}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(item)}
                          aria-label={`編輯 ${item.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDeleteTarget(item)
                            setDeleteConfirmText('')
                          }}
                          aria-label={`刪除 ${item.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog：編輯既有標籤時用頁籤拆成「標籤名稱」與「套用診所」兩種功能 */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className={editingTag ? 'sm:max-w-xl' : undefined}>
          <DialogHeader>
            <DialogTitle>{editingTag ? `編輯標籤「${editingTag.name}」` : '新增標籤'}</DialogTitle>
            <DialogDescription>
              {editingTag
                ? '可重新命名標籤，或切到「套用診所」把此標籤加到多個診所'
                : '建立後可在診所編輯頁的標籤欄位中選用'}
            </DialogDescription>
          </DialogHeader>

          {editingTag ? (
            <Tabs value={editTab} onValueChange={(v) => setEditTab(v as 'name' | 'apply')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="name">標籤名稱</TabsTrigger>
                <TabsTrigger value="apply">
                  <Building2 className="mr-2 h-4 w-4" />
                  套用診所
                </TabsTrigger>
              </TabsList>

              <TabsContent value="name" className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="tag-name">標籤名稱</Label>
                  <Input
                    id="tag-name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    maxLength={TAG_NAME_MAX}
                    placeholder="輸入標籤名稱"
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    最多 {TAG_NAME_MAX} 字；大小寫視為不同（&quot;VIP&quot; 與 &quot;vip&quot; 是兩個標籤）
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setFormOpen(false)} disabled={submitting}>
                    取消
                  </Button>
                  <Button onClick={handleSubmit} disabled={submitting || formName.trim().length === 0}>
                    儲存
                  </Button>
                </DialogFooter>
              </TabsContent>

              <TabsContent value="apply" className="pt-2">
                <ApplyClinicsTab
                  tag={editingTag}
                  providerId={providerId}
                  onApplied={() => mutate()}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="tag-name">標籤名稱</Label>
                <Input
                  id="tag-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  maxLength={TAG_NAME_MAX}
                  placeholder="輸入標籤名稱"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  最多 {TAG_NAME_MAX} 字；大小寫視為不同（&quot;VIP&quot; 與 &quot;vip&quot; 是兩個標籤）
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setFormOpen(false)} disabled={submitting}>
                  取消
                </Button>
                <Button onClick={handleSubmit} disabled={submitting || formName.trim().length === 0}>
                  建立
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete AlertDialog */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null)
            setDeleteConfirmText('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除標籤「{deleteTarget?.name}」？</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                {requiresTypedConfirm ? (
                  <>
                    <p className="text-destructive font-medium">
                      此標籤目前有 {deleteTarget?.usage_count} 間診所正在使用。
                    </p>
                    <p>
                      刪除後將同步從這些診所的標籤欄位移除，且**無法復原**。如要繼續，請於下方輸入完整標籤名稱
                      <span className="font-mono mx-1 px-1 bg-muted rounded">
                        {deleteTarget?.name}
                      </span>
                      確認。
                    </p>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={deleteTarget?.name}
                      className="mt-2"
                      autoFocus
                    />
                  </>
                ) : (
                  <p>目前沒有診所使用此標籤。刪除此標籤不會影響任何診所資料。</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={!canConfirmDelete}>
              確定刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface ApplyClinicsTabProps {
  tag: ProviderTagWithUsage
  providerId: number | null
  // 套用成功後通知外層 refresh 標籤清單（usage_count 會變）
  onApplied: () => void
}

/**
 * 標籤編輯 Dialog 的「套用診所」頁籤：一對多套用 + 顯示已套用狀態（仿照廣告排班模版套用頁）
 */
function ApplyClinicsTab({ tag, providerId, onApplied }: ApplyClinicsTabProps) {
  const { token } = useAuth()
  const { clinics: appliedClinics, mutate: mutateApplications } = useProviderTagApplications({
    tagId: tag.provider_tag_id,
  })
  const { clinics: allClinics } = useAllClinics()

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [isApplying, setIsApplying] = useState(false)
  const [unapplyingId, setUnapplyingId] = useState<number | null>(null)

  const appliedIds = new Set(appliedClinics.map((c) => c.clinic_id))
  const searchLower = searchTerm.toLowerCase()
  // 已套用的診所排最前面，才能一眼看到目前套用狀態並就地取消
  const candidateClinics = allClinics
    .filter((c) => c.provider_id === providerId)
    .filter((c) => c.clinic_name.toLowerCase().includes(searchLower))
    .sort((a, b) => {
      const aApplied = appliedIds.has(a.clinic_id)
      const bApplied = appliedIds.has(b.clinic_id)
      if (aApplied === bApplied) return 0
      return aApplied ? -1 : 1
    })

  const toggle = (clinicId: number) => {
    const next = new Set(selectedIds)
    if (next.has(clinicId)) {
      next.delete(clinicId)
    } else {
      next.add(clinicId)
    }
    setSelectedIds(next)
  }

  const handleApply = async () => {
    if (!token || selectedIds.size === 0) return
    setIsApplying(true)
    try {
      await providerTagService.apply(
        tag.provider_tag_id,
        { clinic_ids: Array.from(selectedIds) },
        token,
      )
      setSelectedIds(new Set())
      mutateApplications()
      onApplied()
    } catch {
      // toast 已在 service 中處理
    } finally {
      setIsApplying(false)
    }
  }

  const handleUnapply = async (clinicId: number) => {
    if (!token) return
    setUnapplyingId(clinicId)
    try {
      await providerTagService.unapply(tag.provider_tag_id, { clinic_ids: [clinicId] }, token)
      mutateApplications()
      onApplied()
    } catch {
      // toast 已在 service 中處理
    } finally {
      setUnapplyingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="搜尋診所..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <ScrollArea className="h-[320px]">
        <div className="space-y-2">
          {candidateClinics.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">沒有符合的診所</p>
          ) : (
            candidateClinics.map((clinic) => {
              const isApplied = appliedIds.has(clinic.clinic_id)
              return (
                <div
                  key={clinic.clinic_id}
                  className={`flex items-center space-x-3 p-3 rounded-lg border ${
                    isApplied ? 'bg-muted/50' : 'hover:bg-muted/50 cursor-pointer'
                  }`}
                  onClick={() => !isApplied && toggle(clinic.clinic_id)}
                >
                  <Checkbox
                    checked={selectedIds.has(clinic.clinic_id) || isApplied}
                    disabled={isApplied}
                    onCheckedChange={() => toggle(clinic.clinic_id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{clinic.clinic_name}</p>
                  </div>
                  {isApplied && (
                    <>
                      <Badge variant="secondary">
                        <Check className="mr-1 h-3 w-3" />
                        已套用
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={unapplyingId === clinic.clinic_id}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleUnapply(clinic.clinic_id)
                        }}
                      >
                        取消套用
                      </Button>
                    </>
                  )}
                </div>
              )
            })
          )}
        </div>
      </ScrollArea>

      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">已選擇 {selectedIds.size} 間診所</p>
        <Button onClick={handleApply} disabled={isApplying || selectedIds.size === 0}>
          套用到選取的診所
        </Button>
      </div>
    </div>
  )
}
